import React, { useState } from "react";
import { Target, Calendar, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { formatCurrency, formatPercent } from "./dashboardUtils";
import { calculateGoalProgress } from "../goals/goalUtils";
import type { Goal, Deal, Sale } from "../types";
import { startOfMonth, endOfMonth, differenceInDays } from "date-fns";

interface GoalsConversionCardProps {
  goals: Goal[];
  deals: Deal[];
  salesTeam: Sale[];
  currency?: string;
}

export const GoalsConversionCard: React.FC<GoalsConversionCardProps> = ({
  goals,
  deals,
  salesTeam,
  currency = "BRL",
}) => {
  const [metricTab, setMetricTab] = useState<"auto" | "volume" | "value">(
    "auto",
  );

  const salesMap = React.useMemo(() => {
    const map = new Map<number, string>();
    salesTeam.forEach((s) => {
      map.set(
        Number(s.id),
        `${s.first_name || ""} ${s.last_name || ""}`.trim(),
      );
    });
    return map;
  }, [salesTeam]);

  // Computa o progresso de cada meta
  const calculatedGoals = React.useMemo(() => {
    return goals.map((goal) => {
      const salesName = goal.sales_id
        ? salesMap.get(Number(goal.sales_id))
        : undefined;
      return calculateGoalProgress(goal, deals, salesName);
    });
  }, [goals, deals, salesMap]);

  // Metas do Mês Atual (Metas × Meses)
  const currentMonthStats = React.useMemo(() => {
    const now = new Date();
    const mStart = startOfMonth(now);
    const mEnd = endOfMonth(now);
    const daysLeft = Math.max(0, differenceInDays(mEnd, now));

    // Filtra metas vigentes no mês atual
    const currentGoals = calculatedGoals.filter((g) => {
      try {
        const start = new Date(g.goal.start_date);
        const end = new Date(g.goal.end_date);
        return start <= mEnd && end >= mStart;
      } catch {
        return false;
      }
    });

    const volumeGoals = currentGoals.filter(
      (g) => g.goal.metric_type === "volume",
    );
    const valueGoals = currentGoals.filter(
      (g) => g.goal.metric_type === "value",
    );

    const hasVolume = volumeGoals.length > 0;
    const hasValue = valueGoals.length > 0;

    // Totais de Volume (Quantidade de negócios / ganhos)
    const volumeTarget = volumeGoals.reduce(
      (acc, g) => acc + g.goal.target_value,
      0,
    );
    const volumeAchieved = volumeGoals.reduce((acc, g) => acc + g.achieved, 0);
    const volumeRemaining = Math.max(0, volumeTarget - volumeAchieved);
    const volumePercent =
      volumeTarget > 0 ? (volumeAchieved / volumeTarget) * 100 : 0;

    // Totais de Valor (Financeiro em R$)
    const valueTarget = valueGoals.reduce(
      (acc, g) => acc + g.goal.target_value,
      0,
    );
    const valueAchieved = valueGoals.reduce((acc, g) => acc + g.achieved, 0);
    const valueRemaining = Math.max(0, valueTarget - valueAchieved);
    const valuePercent =
      valueTarget > 0 ? (valueAchieved / valueTarget) * 100 : 0;

    return {
      hasGoals: currentGoals.length > 0,
      goalsCount: currentGoals.length,
      hasVolume,
      hasValue,
      daysLeft,
      volume: {
        target: volumeTarget,
        achieved: volumeAchieved,
        remaining: volumeRemaining,
        percent: volumePercent,
        count: volumeGoals.length,
      },
      value: {
        target: valueTarget,
        achieved: valueAchieved,
        remaining: valueRemaining,
        percent: valuePercent,
        count: valueGoals.length,
      },
    };
  }, [calculatedGoals]);

  // Determina se exibe em Volume (quantidade) ou Valor (financeiro)
  const activeMode: "volume" | "value" = React.useMemo(() => {
    if (metricTab === "volume") return "volume";
    if (metricTab === "value") return "value";
    // Modo automático: se tiver apenas volume ou a maioria for volume, usa volume
    if (currentMonthStats.hasVolume && !currentMonthStats.hasValue)
      return "volume";
    if (currentMonthStats.hasValue && !currentMonthStats.hasVolume)
      return "value";
    // Se tiver ambos, prefere o que tiver mais metas
    return currentMonthStats.volume.count >= currentMonthStats.value.count
      ? "volume"
      : "value";
  }, [metricTab, currentMonthStats]);

  const activeStats =
    activeMode === "volume"
      ? currentMonthStats.volume
      : currentMonthStats.value;

  const formatDisplayValue = (val: number) => {
    if (activeMode === "volume") {
      const rounded = Math.round(val);
      return `${rounded} ${rounded === 1 ? "ganho" : "ganhos"}`;
    }
    return formatCurrency(val, currency);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "beat":
        return {
          label: "Batida 🌟",
          color: "text-emerald-600 bg-emerald-500/10 border-emerald-500/20",
        };
      case "on_track":
        return {
          label: "No Ritmo 🟢",
          color: "text-blue-600 bg-blue-500/10 border-blue-500/20",
        };
      case "warning":
        return {
          label: "Atenção 🟡",
          color: "text-amber-600 bg-amber-500/10 border-amber-500/20",
        };
      case "late":
        return {
          label: "Atrasada 🔴",
          color: "text-rose-600 bg-rose-500/10 border-rose-500/20",
        };
      default:
        return {
          label: "Finalizada",
          color: "text-muted-foreground bg-muted border-border",
        };
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
      {/* 1. Metas × Meses (Meta do Mês Atual) */}
      <div className="md:col-span-5 bg-card border border-border/80 rounded-xl p-4 shadow-xs flex flex-col justify-between">
        <div>
          <div className="flex items-center justify-between border-b border-border/50 pb-3">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                <Calendar className="w-3.5 h-3.5" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground">
                  Metas × Meses
                </h3>
                <p className="text-[11px] text-muted-foreground">
                  Progresso geral das metas do mês atual
                </p>
              </div>
            </div>
            <span className="text-[11px] font-medium text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
              {currentMonthStats.daysLeft} dias restantes
            </span>
          </div>

          <div className="mt-4">
            {!currentMonthStats.hasGoals ? (
              <div className="text-center py-8 text-xs text-muted-foreground space-y-2">
                <p>Nenhuma meta ativa cadastrada para o mês atual.</p>
                <Link
                  to="/metas"
                  className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                >
                  Criar meta agora <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Se existirem ambos os tipos de meta (volume e valor), permite alternar */}
                {currentMonthStats.hasVolume && currentMonthStats.hasValue && (
                  <div className="flex items-center gap-1.5 p-1 bg-muted/60 rounded-lg text-xs">
                    <button
                      type="button"
                      onClick={() => setMetricTab("volume")}
                      className={`flex-1 py-1 px-2 rounded-md font-medium transition-all ${
                        activeMode === "volume"
                          ? "bg-card text-foreground shadow-xs"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      Quantidade ({currentMonthStats.volume.count})
                    </button>
                    <button
                      type="button"
                      onClick={() => setMetricTab("value")}
                      className={`flex-1 py-1 px-2 rounded-md font-medium transition-all ${
                        activeMode === "value"
                          ? "bg-card text-foreground shadow-xs"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      Financeiro ({currentMonthStats.value.count})
                    </button>
                  </div>
                )}

                {/* Métricas do mês */}
                <div className="flex items-baseline justify-between">
                  <div>
                    <span className="text-xs text-muted-foreground block">
                      Atingido no mês (
                      {activeMode === "volume" ? "quantidade" : "financeiro"})
                    </span>
                    <span className="text-2xl font-bold text-foreground">
                      {formatDisplayValue(activeStats.achieved)}
                    </span>
                  </div>
                  <div className="text-right">
                    <span className="text-xs text-muted-foreground block">
                      Meta do mês
                    </span>
                    <span className="text-sm font-semibold text-muted-foreground">
                      {formatDisplayValue(activeStats.target)}
                    </span>
                  </div>
                </div>

                {/* Barra de Progresso */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between text-xs font-semibold">
                    <span className="text-primary">
                      {formatPercent(activeStats.percent)} concluído
                    </span>
                    <span className="text-muted-foreground">
                      Falta: {formatDisplayValue(activeStats.remaining)}
                    </span>
                  </div>
                  <div className="w-full h-3 rounded-full bg-muted overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${
                        activeStats.percent >= 100
                          ? "bg-emerald-500"
                          : activeStats.percent >= 70
                            ? "bg-primary"
                            : "bg-amber-500"
                      }`}
                      style={{
                        width: `${Math.min(activeStats.percent, 100)}%`,
                      }}
                    />
                  </div>
                </div>

                <div className="p-2.5 rounded-lg bg-muted/40 border border-border/40 text-xs text-muted-foreground flex items-center justify-between">
                  <span>Metas ativas no mês:</span>
                  <span className="font-semibold text-foreground">
                    {currentMonthStats.goalsCount}{" "}
                    {currentMonthStats.goalsCount === 1 ? "meta" : "metas"}{" "}
                    {activeMode === "volume"
                      ? "(por quantidade)"
                      : "(em valor)"}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="pt-3 border-t border-border/40 mt-3 text-right">
          <Link
            to="/metas"
            className="text-xs text-primary font-medium hover:underline inline-flex items-center gap-1"
          >
            Gerenciar todas as metas <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </div>

      {/* 2. Conversão × Metas (Percentual por Meta Cadastrada) */}
      <div className="md:col-span-7 bg-card border border-border/80 rounded-xl p-4 shadow-xs">
        <div className="flex items-center justify-between border-b border-border/50 pb-3">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-emerald-500/10 text-emerald-500 flex items-center justify-center">
              <Target className="w-3.5 h-3.5" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-foreground">
                Conversão × Metas
              </h3>
              <p className="text-[11px] text-muted-foreground">
                Percentual de conclusão individual de cada meta cadastrada
              </p>
            </div>
          </div>
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
            {calculatedGoals.length} cadastradas
          </span>
        </div>

        <div className="mt-4 space-y-3 max-h-[320px] overflow-y-auto pr-1">
          {calculatedGoals.length === 0 ? (
            <div className="text-center py-10 text-xs text-muted-foreground">
              Nenhuma meta cadastrada ainda.
            </div>
          ) : (
            calculatedGoals.map((p) => {
              const badge = getStatusBadge(p.status);
              const isValue = p.goal.metric_type === "value";
              return (
                <div
                  key={p.goal.id}
                  className="p-2.5 rounded-lg border border-border/50 bg-muted/20 space-y-2 hover:border-border transition-all"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <span className="text-xs font-semibold text-foreground block">
                        {p.goal.name}
                      </span>
                      <span className="text-[10px] text-muted-foreground">
                        {p.goal.scope === "usuario"
                          ? `Individual (${p.salesPersonName || "Colaborador"})`
                          : "Equipe toda"}{" "}
                        • Tipo:{" "}
                        {isValue ? "Financeiro (R$)" : "Quantidade de Ganhos"} •
                        Etapa: {p.goal.stage}
                      </span>
                    </div>
                    <span
                      className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${badge.color}`}
                    >
                      {badge.label}
                    </span>
                  </div>

                  <div className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">
                        {isValue
                          ? formatCurrency(p.achieved, currency)
                          : `${p.achieved} ${p.achieved === 1 ? "ganho" : "ganhos"}`}{" "}
                        de{" "}
                        <span className="font-semibold text-foreground">
                          {isValue
                            ? formatCurrency(p.goal.target_value, currency)
                            : `${p.goal.target_value} ${p.goal.target_value === 1 ? "ganho" : "ganhos"}`}
                        </span>
                      </span>
                      <span className="font-bold text-foreground">
                        {formatPercent(p.percent)}
                      </span>
                    </div>

                    <div className="w-full h-2 rounded-full bg-muted overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${
                          p.percent >= 100
                            ? "bg-emerald-500"
                            : p.percent >= 70
                              ? "bg-primary"
                              : "bg-amber-500"
                        }`}
                        style={{ width: `${Math.min(p.percent, 100)}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};
